import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import type { ItemOut, ReviewStatus } from '@/api/research';
import { qk } from '@/api/queries';
import { Button, Icon, IconButton, Modal, ModalFooter, ModalHeader, Pill, SearchInput, Segmented, useModalTitleId, useToast, type Tone } from '@/design-system';
import { cx } from '@/lib/cx';
import { formatNumber, formatSum } from '@/lib/format';
import { errorText } from '@/shell/errorText';
import { LoadError } from '@/shell/LoadError';
import { websitesQuery, type Website } from '@/features/sites/api';
import { ImportModal } from '@/features/store/ImportModal';
import { approveSnapshot, bulk, merge, patchItem, productsQuery, updateCachedItems } from './api';
import { EditItemDrawer } from './EditItemDrawer';
import s from './research.module.css';

/** The row height in the design (34px thumb + 2×10px padding + the 1px divider): the virtual list's unit. */
export const ROW_H = 55;
/** Up to this many rows render plainly, exactly as the design; beyond it the list is windowed. */
const PLAIN_MAX = 60;
const OVERSCAN = 12;

const STATUS_TONE: Record<ReviewStatus, Tone> = { approved: 'ok', new: 'warn', rejected: 'err' };
type Filter = 'all' | ReviewStatus;

const nameOf = (i: ItemOut) => String(i.data.name ?? '');

/** The rows in view (the page scrolls, not the table): indices [start, end) with an overscan. */
function useWindowRange(ref: RefObject<HTMLDivElement | null>, count: number, enabled: boolean) {
  const [range, setRange] = useState({ start: 0, end: Math.min(count, 40) });
  useLayoutEffect(() => {
    if (!enabled) return;
    const update = () => {
      const top = ref.current?.getBoundingClientRect().top ?? 0;
      const first = Math.floor(Math.max(0, -top) / ROW_H);
      const last = Math.ceil(Math.max(0, window.innerHeight - top) / ROW_H);
      const start = Math.max(0, first - OVERSCAN);
      const end = Math.min(count, last + OVERSCAN);
      setRange((r) => (r.start === start && r.end === end ? r : { start, end }));
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [ref, count, enabled]);
  return enabled ? range : { start: 0, end: count };
}

/** «Товары из Research · отчёт vN» (DESIGN_SPEC §4.3, §9 C9; P2-D03): review at catalog scale. */
/** The website «Отправить в магазин» imports into: the given one, else the organization's furthest e-commerce site. */
const STORE_ORDER: Website['status'][] = ['production', 'pending_production', 'demo_published', 'preview_ready', 'designing', 'research_ready', 'draft'];
export function importTarget(sites: Website[] | undefined, siteId?: string): string | null {
  if (siteId) return siteId;
  const shop = (sites ?? []).filter((w) => w.type === 'ecommerce' && w.status !== 'archived');
  shop.sort((a, b) => STORE_ORDER.indexOf(a.status) - STORE_ORDER.indexOf(b.status));
  return shop[0]?.id ?? null;
}

export function ItemsTable({ orgId, siteId, nextVersion }: { orgId: string; siteId?: string; nextVersion: number }) {
  const { t } = useTranslation('research');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(id);
  }, [search]);
  const query = useInfiniteQuery(productsQuery(orgId, { ...(filter !== 'all' ? { review_status: filter } : {}), ...(q ? { search: q } : {}) }));
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // «Отправить в магазин» (P5-E03): the import modal for the website (the selection, else every approved item).
  const sites = useQuery({ ...websitesQuery(orgId), enabled: !siteId });
  const [importing, setImporting] = useState<{ siteId: string; ids: string[] } | null>(null);
  const [editing, setEditing] = useState<ItemOut | null>(null);
  const [merging, setMerging] = useState(false);
  const body = useRef<HTMLDivElement>(null);
  const virtual = items.length > PLAIN_MAX;
  const range = useWindowRange(body, items.length, virtual);

  // Load the next page as the window nears the end of what is loaded.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && range.end >= items.length - OVERSCAN) void fetchNextPage();
  }, [range.end, items.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['research', 'items', orgId] });
    void qc.invalidateQueries({ queryKey: qk.researchRuns(orgId) });
  };
  const review = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ReviewStatus }) => patchItem(id, { review_status: status }),
    onMutate: ({ id, status }) => updateCachedItems(qc, orgId, (xs) => xs.map((x) => (x.id === id ? { ...x, review_status: status } : x))),
    onError: (err) => {
      toast(errorText(err, tc));
      refresh();
    },
  });
  const bulkAct = useMutation({
    mutationFn: (action: 'approve' | 'reject' | 'delete') => bulk({ ids: [...selected], action }),
    onMutate: (action) => {
      const ids = selected;
      updateCachedItems(qc, orgId, (xs) =>
        action === 'delete' ? xs.filter((x) => !ids.has(x.id)) : xs.map((x) => (ids.has(x.id) ? { ...x, review_status: action === 'approve' ? 'approved' : 'rejected' } : x)),
      );
    },
    onSuccess: (r) => {
      setSelected(new Set());
      toast(t('items.bulk_done', { count: r.updated }));
      refresh();
    },
    onError: (err) => {
      toast(errorText(err, tc));
      refresh();
    },
  });
  const snapshot = useMutation({
    mutationFn: () => approveSnapshot(orgId),
    onSuccess: (snap) => {
      toast(t('items.snapshot_done', { version: snap.version, count: snap.item_count }));
      void qc.invalidateQueries({ queryKey: qk.researchSnapshots(orgId) });
    },
    onError: (err) => toast(errorText(err, tc)),
  });

  const toggle = (id: string) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const approvedCount = items.filter((x) => x.review_status === 'approved').length;
  const selectedItems = items.filter((x) => selected.has(x.id));
  const filtered = filter !== 'all' || !!q;
  const [tools, setTools] = useState<boolean | null>(null);
  const toolsOpen = tools ?? (filtered || total > 20);

  const row = (r: ItemOut) => {
    const sel = selected.has(r.id);
    const conf = Math.round(r.confidence * 100);
    const price = r.data.price === null || r.data.price === undefined || r.data.price === '' ? null : Number(r.data.price);
    const category = r.data.category ? String(r.data.category) : tc('dash');
    return (
      <div key={r.id} role="row" className={cx(s.row, sel && s.rowSel)}>
        <span role="cell">
          <button type="button" className={cx(s.check, sel && s.checkOn)} aria-pressed={sel} aria-label={t('items.select', { name: nameOf(r) })} onClick={() => toggle(r.id)}>
            {sel && <Icon name="check" size={13} />}
          </button>
        </span>
        <div role="cell" className={s.product}>
          <div className={s.thumb} />
          {/* The name opens the item (edit, versions, delete: §9 C9, additive). */}
          <button type="button" className={s.name} onClick={() => setEditing(r)}>
            {nameOf(r)}
          </button>
        </div>
        <span role="cell" className={s.cat}>
          {category}
        </span>
        <div role="cell" className={s.priceCell}>
          <span className={s.priceValue}>{price === null ? tc('dash') : formatSum(price)}</span>
          {/* A price read from the item's photo (P8-X02) says so: «с фото», still unverified. */}
          <span className={s.priceNote}>
            {price !== null && !r.price_verified ? (r.image_facts?.some((f) => f.field === 'price' && f.applied) ? `${t('items.from_image')} · ${t('items.unverified')}` : t('items.unverified')) : ''}
          </span>
        </div>
        <span role="cell" className={s.cat}>
          {t(`items.source.${r.source_kind as 'website'}`, { defaultValue: r.source_kind })}
        </span>
        <div role="cell" className={s.conf}>
          <div className={s.confTrack}>
            <div className={cx(s.confFill, conf >= 85 ? s.confOk : conf >= 65 ? s.confWarn : s.confErr)} style={{ width: `${conf}%` }} />
          </div>
          <span className={s.confValue}>{conf}%</span>
        </div>
        <span role="cell" className={s.statusCell}>
          <Pill tone={STATUS_TONE[r.review_status]} size="sm">
            {t(`items.status.${r.review_status}`)}
          </Pill>
        </span>
        <div role="cell" className={s.rowActions}>
          <IconButton icon="check" label={t('items.approve')} tone="outline" size={30} iconSize={14} className={s.okBtn} onClick={() => review.mutate({ id: r.id, status: 'approved' })} />
          <IconButton icon="x" label={t('items.reject')} tone="outline" size={30} iconSize={14} className={s.noBtn} onClick={() => review.mutate({ id: r.id, status: 'rejected' })} />
        </div>
      </div>
    );
  };

  const visible = items.slice(range.start, range.end);
  return (
    <div className={s.tableCard}>
      <div className={s.tableInner}>
        <div className={s.tableHead}>
          <span className={s.tableTitle}>
            {t('items.title')}{' '}
            <span className={s.tableReport}>{t('items.report', { version: nextVersion })}</span>
          </span>
          <span className={s.selTxt}>{selected.size ? t('items.selected', { count: selected.size }) : t('items.approved', { approved: approvedCount, total: items.length })}</span>
          <Button variant="secondary" size={34} icon="check" iconSize={14} className={s.small} disabled={!selected.size || bulkAct.isPending} onClick={() => bulkAct.mutate('approve')}>
            {t('items.approve')}
          </Button>
          <Button variant="secondary" size={34} icon="x" iconSize={14} className={s.small} disabled={!selected.size || bulkAct.isPending} onClick={() => bulkAct.mutate('reject')}>
            {t('items.reject')}
          </Button>
          {/* Merge, delete and the tools bar are additive (§9 C9): round icon buttons, so the row never wraps. */}
          {selected.size >= 2 && <IconButton icon="layers" label={t('items.merge')} tone="outline" size={34} iconSize={14} onClick={() => setMerging(true)} />}
          {selected.size > 0 && (
            <IconButton
              icon="trash-2"
              label={t('items.delete')}
              tone="outline"
              size={34}
              iconSize={14}
              onClick={() => {
                if (window.confirm(t('items.confirm_delete'))) bulkAct.mutate('delete');
              }}
            />
          )}
          <IconButton icon="sliders-horizontal" label={t('items.tools')} tone="outline" size={34} iconSize={14} aria-pressed={toolsOpen} onClick={() => setTools((v) => !v)} />
          <Button
            size={34}
            icon="send"
            iconSize={14}
            className={s.send}
            onClick={() => {
              const target = importTarget(sites.data, siteId);
              if (!target) return toast(t('items.send_no_site'));
              setImporting({ siteId: target, ids: [...selected] });
            }}
          >
            {t('items.send')}
          </Button>
        </div>
        <div className={s.cols} role="row">
          <span />
          <span>{t('items.cols.product')}</span>
          <span>{t('items.cols.category')}</span>
          <span>{t('items.cols.price')}</span>
          <span>{t('items.cols.source')}</span>
          <span>{t('items.cols.confidence')}</span>
          <span>{t('items.cols.status')}</span>
          <span />
        </div>
        <div ref={body} role="table" aria-label={t('items.label')} aria-rowcount={total}>
          {query.isError ? (
            <LoadError error={query.error} onRetry={() => void query.refetch()} />
          ) : (
            <>
              {virtual && <div style={{ height: range.start * ROW_H }} aria-hidden />}
              {visible.map(row)}
              {virtual && <div style={{ height: Math.max(0, items.length - range.end) * ROW_H }} aria-hidden />}
              {query.data && items.length === 0 && <div className={s.tableEmpty}>{filtered ? t('items.empty_filtered') : t('items.empty')}</div>}
            </>
          )}
        </div>
        {/* Paging, filters, search and the report approval (§9 C9): a bar under the rows, open by the toggle
            or by itself for a catalog longer than a screen or while a filter is on. */}
        {toolsOpen && (
        <div className={s.tableFoot}>
          <SearchInput value={search} onChange={setSearch} placeholder={t('items.search')} label={t('items.search')} className={s.footSearch} />
          <Segmented
            label={t('items.filter_label')}
            value={filter}
            onChange={(v) => {
              setFilter(v);
              setSelected(new Set());
            }}
            options={(['all', 'new', 'approved', 'rejected'] as const).map((k) => ({ value: k, label: t(`items.filters.${k}`) }))}
          />
          <span className={s.shown}>{t('items.shown', { shown: formatNumber(items.length), total: formatNumber(total) })}</span>
          <Button variant="light" size={34} icon="badge-check" iconSize={14} className={s.small} disabled={snapshot.isPending} onClick={() => snapshot.mutate()}>
            {t('items.snapshot', { version: nextVersion })}
          </Button>
        </div>
        )}
      </div>
      {importing && <ImportModal siteId={importing.siteId} orgId={orgId} productIds={importing.ids.length ? importing.ids : undefined} open onClose={() => setImporting(null)} />}
      {editing && (
        <EditItemDrawer
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={(next) => {
            updateCachedItems(qc, orgId, (xs) => xs.map((x) => (x.id === next.id ? next : x)));
            refresh();
          }}
          onDelete={() => {
            setSelected(new Set([editing.id]));
            void bulk({ ids: [editing.id], action: 'delete' }).then(() => {
              updateCachedItems(qc, orgId, (xs) => xs.filter((x) => x.id !== editing.id));
              setSelected(new Set());
              setEditing(null);
              refresh();
            });
          }}
        />
      )}
      {merging && <MergeModal items={selectedItems} onClose={() => setMerging(false)} onDone={() => {
        setSelected(new Set());
        setMerging(false);
        refresh();
      }} />}
    </div>
  );
}

/** «Объединить»: choose which item stays; the others become its duplicates. POST /admin/research-items/merge. */
function MergeModal({ items, onClose, onDone }: { items: ItemOut[]; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('research');
  const { t: tc } = useTranslation('common');
  const titleId = useModalTitleId();
  const toast = useToast();
  const [keep, setKeep] = useState(items[0]?.id ?? '');
  const run = useMutation({
    mutationFn: () => merge(items.map((x) => x.id), keep),
    onSuccess: (kept) => {
      toast(t('items.merged', { name: nameOf(kept) }));
      onDone();
    },
    onError: (err) => toast(errorText(err, tc)),
  });
  return (
    <Modal open onClose={onClose} labelledBy={titleId}>
      <ModalHeader id={titleId} title={t('items.merge_title')} sub={t('items.merge_sub')} onClose={onClose} closeLabel={tc('actions.close')} />
      <div className={s.mergeList} role="radiogroup" aria-label={t('items.merge_keep')}>
        {items.map((x) => (
          <button key={x.id} type="button" role="radio" aria-checked={keep === x.id} className={cx(s.mergeRow, keep === x.id && s.mergeOn)} onClick={() => setKeep(x.id)}>
            <span className={cx(s.check, keep === x.id && s.checkOn)}>{keep === x.id && <Icon name="check" size={13} />}</span>
            <span className={s.mergeName}>{nameOf(x)}</span>
            <span className={s.confValue}>{Math.round(x.confidence * 100)}%</span>
          </button>
        ))}
      </div>
      <ModalFooter>
        <Button size={46} variant="secondary" onClick={onClose}>
          {tc('actions.cancel')}
        </Button>
        <Button size={46} onClick={() => run.mutate()} disabled={run.isPending || !keep}>
          {t('items.merge')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
